import React, { useState, useEffect } from "react";
import Layout from '../pages/Layout';
import Advertise from "../pages/Advertise";

const ExpenseRatio = () => {
  // State variables for Expense Ratio calculation
  const [totalFundExpenses, setTotalFundExpenses] = useState(0); // Total fund expenses
  const [totalAssets, setTotalAssets] = useState(0); // Total assets under management
  const [expenseRatio, setExpenseRatio] = useState(0); // Calculated expense ratio

  // Event handlers to update state values
  const handleTotalFundExpensesChange = (e) => setTotalFundExpenses(parseFloat(e.target.value) || 0);
  const handleTotalAssetsChange = (e) => setTotalAssets(parseFloat(e.target.value) || 0);

  // useEffect hook to calculate the expense ratio when any input changes
  useEffect(() => {
    if (totalFundExpenses && totalAssets) {
      const ratio = (totalFundExpenses / totalAssets) * 100; // Calculate expense ratio
      setExpenseRatio(ratio);
    } else {
      setExpenseRatio(0);
    }
    
    // This will initialize or reinitialize all popovers on the page whenever the component renders
    const popoverTriggerList = [].slice.call(
      document.querySelectorAll('[data-bs-toggle="popover"]')
    );

    // Clean up any existing popover instances
    popoverTriggerList.forEach((popoverTriggerEl) => {
      const popover = window.bootstrap.Popover.getInstance(popoverTriggerEl); // Check if instance already exists
      if (popover) {
        popover.dispose(); // Dispose of existing popover
      }
    });

    // Re-initialize popovers
    popoverTriggerList.forEach((popoverTriggerEl) => {
      new window.bootstrap.Popover(popoverTriggerEl); // Re-initialize popovers
    });

  }, [totalFundExpenses, totalAssets]);

  return (
    <Layout title="Expense Ratio Calculator" path="expense-ratio">
      <section className="section-padding" id="expense-ratio-calculator">
        <div className="container mt-5">
          <div className="row">
            {/* Expense Ratio Calculator Form */}
            <div className="col-lg-9 col-md-8 mb-4">
              <div
                className="p-4 border rounded shadow"
                style={{ border: "1px solid black" }}
              >
                <h3 className="text-center mb-4">Expense Ratio Calculator</h3>
                <div className="container">
                  <div className="row gy-3">
                    {/* Total Fund Expenses Input */}
                    <div className="col-md-6 col-lg-4">
                      <label htmlFor="totalFundExpenses" className="form-label">
                        Total Fund Expenses (₹)
                        <span
                    className="ms-2 text-muted"
                    data-bs-toggle="popover"
                    data-bs-html="true"
                    data-bs-placement="top"
                    data-bs-content="<strong>Total Fund Expenses:</strong> The total expenses incurred by the fund, including management fees, operating costs, etc."
                    style={{ cursor: "pointer" }}
                  >
                    <i className="bi bi-info-circle"></i>
                  </span>
                      </label>
                      <input
                        type="number"
                        className="form-control"
                        id="totalFundExpenses"
                        placeholder="Enter total fund expenses"
                        value={totalFundExpenses || ""}
                        onChange={handleTotalFundExpensesChange}
                      />
                    </div>

                    {/* Total Assets Input */}
                    <div className="col-md-6 col-lg-4">
                      <label htmlFor="totalAssets" className="form-label">
                        Total Assets (₹)
                        <span
                    className="ms-2 text-muted"
                    data-bs-toggle="popover"
                    data-bs-html="true"
                    data-bs-placement="top"
                    data-bs-content="<strong>Total Assets:</strong> The total value of assets under management in the fund."
                    style={{ cursor: "pointer" }}
                  >
                    <i className="bi bi-info-circle"></i>
                  </span>
                      </label>
                      <input
                        type="number"
                        className="form-control"
                        id="totalAssets"
                        placeholder="Enter total assets under management"
                        value={totalAssets || ""}
                        onChange={handleTotalAssetsChange}
                      />
                    </div>
                  </div>

                  {/* Result Section */}
                  <div className="mt-4">
                    <div className="p-3 text-center">
                      <h6>
                        Expense Ratio:{" "}
                        <span className="fw-bold ms-3">
                          {expenseRatio === 0 ? "0.00" : expenseRatio.toFixed(2)} %
                        </span>
                      </h6>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Advertisement Section */}
            <Advertise />
          </div>
        </div>
      </section>

      {/* Introduction Section */}
      <section className="introduction-section" id="introduction">
        <div className="container">
          <div className="row">
            <div className="col-lg-12">
              <div className="p-5">
                <h3 className="text-center mb-5">
                  Introduction to the Expense Ratio Calculator
                </h3>

                {/* What is an Expense Ratio Calculator? */}
                <h4 className="mt-3 mb-3">What is an Expense Ratio Calculator?</h4>
                <p>
                  An **Expense Ratio Calculator** helps you estimate the annual operating expenses of an investment fund as a percentage of its total assets. This ratio is important to understand how much of your investment goes toward fund management fees and expenses.
                </p>

                {/* Why Use an Expense Ratio Calculator? */}
                <h4 className="mt-4 mb-3">Why Use an Expense Ratio Calculator?</h4>
                <p>
                  This calculator helps investors evaluate the cost of owning mutual funds or exchange-traded funds (ETFs). By calculating the expense ratio, it provides insight into the cost-effectiveness of a fund and aids in comparing different investment options.
                </p>

                {/* Benefits of Using an Expense Ratio Calculator */}
                <h4 className="mt-4 mb-3">Benefits of Using an Expense Ratio Calculator</h4>
                <ul>
                  <li><strong>Helps Investors Understand the Cost of Owning Mutual Funds or ETFs:</strong> Provides a clearer picture of how much you are paying in fees compared to potential returns.</li>
                  <li><strong>Assists in Evaluating Fund Management Fees:</strong> Allows you to assess if the fund’s fees are reasonable in relation to its performance.</li>
                  <li><strong>Useful for Comparing Investment Options:</strong> Helps compare different investment funds, allowing you to choose the one with the lowest fees and best returns.</li>
                </ul>

                {/* Suitable For */}
                <h4 className="mt-4 mb-3">Who Should Use an Expense Ratio Calculator?</h4>
                <p>
                  The Expense Ratio Calculator is suitable for:
                </p>
                <ul>
                  <li><strong>Investors:</strong> Helps individual investors assess the cost of investing in different funds.</li>
                  <li><strong>Financial Advisors:</strong> Assists financial professionals in recommending investment funds with optimal fees.</li>
                  <li><strong>Mutual Fund Companies:</strong> Provides insight into how their fund’s fees compare to others in the market.</li>
                </ul>

                {/* Additional Questions */}
                <h4 className="mt-4 mb-3">Why is a low expense ratio important?</h4>
                <p>
                  A low expense ratio means lower costs to the investor, which can lead to higher net returns over time. Over the long term, even small differences in expense ratios can have a significant impact on investment growth.
                </p>

                <h4 className="mt-4 mb-3">What should I consider when comparing expense ratios?</h4>
                <p>
                  While a lower expense ratio is generally better, it's also important to consider other factors such as the fund’s historical performance, its risk level, and the value it provides in terms of management expertise.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default ExpenseRatio;
